import { useEffect, useState } from "react";
import { Mail, Phone, Trash2, CalendarDays, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["nouvelle", "en discussion", "confirmée", "refusée"];

const STATUS_STYLE = {
  "nouvelle": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "en discussion": "bg-sky-500/15 text-sky-400 border-sky-500/30",
  "confirmée": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "refusée": "bg-red-500/15 text-red-400 border-red-500/30",
};

const selectCls =
  "bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-lg px-3 py-1.5 text-xs text-zinc-200";

export default function DemandesTab() {
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/booking-requests");
      setItems(data);
    } catch (err) {
      toast.error("Chargement impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patchStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/booking-requests/${id}`, { status });
      setItems((list) => list.map((it) => (it.id === id ? data : it)));
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error("Échec de la mise à jour", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/booking-requests/${id}`);
      setItems((list) => list.filter((it) => it.id !== id));
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error("Suppression impossible", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  if (items === null) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-amber-500" size={28} data-testid="demandes-loading" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="demandes-empty-state" className="rounded-2xl border border-dashed border-zinc-800 py-24 flex flex-col items-center gap-3 text-center">
        <Inbox size={32} className="text-zinc-700" />
        <p className="text-zinc-400 font-semibold">Aucune demande pour le moment</p>
        <p className="text-sm text-zinc-600 max-w-sm">
          Les demandes envoyées via le formulaire « Demander un concert » apparaîtront ici, et vous serez aussi notifié par email.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="demandes-list" className="grid md:grid-cols-2 gap-4">
      {items.map((b) => (
        <article
          key={b.id}
          data-testid={`booking-card-${b.id}`}
          className="rounded-2xl border border-zinc-800 bg-[#161622] p-5 hover:border-amber-500/30 transition-colors duration-300"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-display font-bold uppercase tracking-wide leading-tight">{b.collectivite}</h3>
              <p className="text-xs text-zinc-500 mt-1">{b.contact_name}</p>
            </div>
            <span className={`text-[11px] font-jbmono uppercase tracking-wider border rounded-full px-3 py-1 shrink-0 ${STATUS_STYLE[b.status] || STATUS_STYLE.nouvelle}`}>
              {b.status}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-zinc-400 mb-4">
            <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
              <Mail size={12} /> {b.email}
            </a>
            {b.phone && (
              <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                <Phone size={12} /> {b.phone}
              </a>
            )}
            {b.event_date && (
              <span className="flex items-center gap-1.5">
                <CalendarDays size={12} /> {b.event_date}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 text-[11px] font-jbmono uppercase tracking-wider text-zinc-500 mb-4">
            <span className="border border-zinc-800 rounded-full px-3 py-1">{b.event_type}</span>
            {b.budget && <span className="border border-zinc-800 rounded-full px-3 py-1">Budget : {b.budget}</span>}
          </div>

          {b.message && <p className="text-sm text-zinc-400 leading-relaxed mb-4 whitespace-pre-line">{b.message}</p>}

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-zinc-800/70">
            <select
              data-testid={`booking-status-select-${b.id}`}
              value={b.status}
              onChange={(e) => patchStatus(b.id, e.target.value)}
              className={selectCls}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              data-testid={`booking-delete-${b.id}`}
              onClick={() => remove(b.id)}
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-red-400 transition-colors duration-300"
            >
              <Trash2 size={13} />
              Supprimer
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
